/*eslint max-lines-per-function: ["error", 280]*/
/*eslint complexity: ["error", 30]*/
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import cronstrue from 'cronstrue/i18n';

type PeriodType = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';

interface CronBuilderProps {
  value: string;
  onChange: (cron: string) => void;
  disabled?: boolean;
  language: 'PL' | string;
}

const DAYS_PL = [
  'niedziela',
  'poniedziałek',
  'wtorek',
  'środa',
  'czwartek',
  'piątek',
  'sobota',
];
const DAYS_EN = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const MONTHS_PL = [
  'styczeń',
  'luty',
  'marzec',
  'kwiecień',
  'maj',
  'czerwiec',
  'lipiec',
  'sierpień',
  'wrzesień',
  'październik',
  'listopad',
  'grudzień',
];
const MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const PERIOD_LABELS: Record<PeriodType, { PL: string; EN: string }> = {
  hourly: { PL: 'Godzina', EN: 'Hourly' },
  daily: { PL: 'Dzień', EN: 'Daily' },
  weekly: { PL: 'Tydzień', EN: 'Weekly' },
  monthly: { PL: 'Miesiąc', EN: 'Monthly' },
  yearly: { PL: 'Rok', EN: 'Yearly' },
};

const T: Record<string, { PL: string; EN: string }> = {
  period: { PL: 'Czas', EN: 'Period' },
  minute: { PL: 'Minuta', EN: 'Minute' },
  minuteStep: { PL: 'Co ile minut', EN: 'Every N minutes' },
  hour: { PL: 'Godzina', EN: 'Hour' },
  hourFrom: { PL: 'Od godziny', EN: 'From hour' },
  hourTo: { PL: 'Do godziny', EN: 'To hour' },
  hourToNone: { PL: 'Bez limitu', EN: 'No limit' },
  hourAll: { PL: 'Wszystkie godziny', EN: 'All hours' },
  hours: { PL: 'Godziny', EN: 'Hours' },
  day: { PL: 'Dzień', EN: 'Day' },
  month: { PL: 'Miesiąc', EN: 'Month' },
  daysOfWeek: { PL: 'Dni tygodnia', EN: 'Days of week' },
  atEvery: { PL: 'W każdym', EN: 'At every' },
  at: { PL: 'O', EN: 'At' },
  on: { PL: 'W dniu', EN: 'On day' },
  inMonth: { PL: 'W miesiącu', EN: 'In month' },
  description: { PL: 'Opis', EN: 'Description' },
  cronExpr: { PL: 'Wyrażenie', EN: 'Expression' },
};

function t(key: string, lang: string): string {
  return T[key]?.[lang === 'PL' ? 'PL' : 'EN'] ?? key;
}

interface CronState {
  period: PeriodType;
  minute: number; // exact minute (daily/weekly) or step (hourly)
  hour: number; // exact hour for daily/weekly/monthly/yearly
  hours: number[]; // specific hours for hourly (empty = all hours)
  daysOfWeek: number[]; // selected weekdays (hourly too; empty = all)
  dayOfMonth: number;
  month: number;
}

// Extract first numeric value from a cron field, handling */N, N-M, N,M etc.
function firstNum(field: string, fallback: number): number {
  if (field === '*') {
    return fallback;
  }
  // */N  → treat as N (step)
  const stepMatch = field.match(/^\*\/(\d+)$/);
  if (stepMatch) {
    return Number(stepMatch[1]);
  }
  // take first number (before any -, ,)
  const plain = Number(field.split(/[-,/]/)[0]);
  return isNaN(plain) ? fallback : plain;
}

// Expand a cron field like "8,21,22" or "9-23" or "*" into an array of numbers.
function expandField(field: string): number[] {
  if (field === '*') {
    return [];
  }
  return field
    .split(',')
    .flatMap(part => {
      const m = part.match(/^(\d+)-(\d+)$/);
      if (m) {
        const from = Number(m[1]);
        const to = Number(m[2]);
        return Array.from({ length: to - from + 1 }, (_, i) => from + i);
      }
      return [Number(part)];
    })
    .filter(n => !isNaN(n));
}

// Compress a sorted array of numbers to minimal cron field (detects contiguous ranges).
function compressHours(hours: number[]): string {
  if (hours.length === 0) {
    return '*';
  }
  const sorted = [...hours].sort((a, b) => a - b);
  const segments: string[] = [];
  let start = sorted[0];
  let end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      segments.push(start === end ? String(start) : `${start}-${end}`);
      start = sorted[i];
      end = sorted[i];
    }
  }
  segments.push(start === end ? String(start) : `${start}-${end}`);
  return segments.join(',');
}

function parseCron(cron: string): CronState {
  const parts = (cron || '0 8 * * 1-5').trim().split(/\s+/);
  if (parts.length !== 5) {
    return {
      period: 'daily',
      minute: 0,
      hour: 8,
      hours: [],
      dayOfMonth: 1,
      month: 1,
      daysOfWeek: [1],
    };
  }
  const [mm, hh, dom, mon, dow] = parts;

  // Treat as hourly/interval when minute field is a step (*/N or *)
  const isInterval = mm === '*' || mm.startsWith('*/');

  let period: PeriodType = 'daily';
  if (isInterval) {
    // interval mode — always map to 'hourly', DOW handled via daysOfWeek
    period = 'hourly';
  } else if (dow !== '*') {
    period = 'weekly';
  } else if (dom !== '*' && mon !== '*') {
    period = 'yearly';
  } else if (dom !== '*') {
    period = 'monthly';
  }

  return {
    period,
    minute: isInterval
      ? mm === '*'
        ? 1
        : Number(mm.replace('*/', ''))
      : firstNum(mm, 0),
    hour: firstNum(hh, 8),
    hours: isInterval ? expandField(hh) : [],
    dayOfMonth: dom === '*' ? 1 : firstNum(dom, 1),
    month: mon === '*' ? 1 : firstNum(mon, 1),
    daysOfWeek: dow === '*' ? [] : expandField(dow),
  };
}

function buildCron(state: CronState): string {
  const { period, minute, hour, hours, dayOfMonth, month, daysOfWeek } = state;
  if (period === 'hourly') {
    const mm = minute <= 1 ? '*' : `*/${minute}`;
    const hh = compressHours(hours);
    const dow = daysOfWeek.length === 0 ? '*' : compressHours(daysOfWeek);
    return `${mm} ${hh} * * ${dow}`;
  }
  switch (period) {
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekly': {
      const dow = daysOfWeek.length === 0 ? '1' : compressHours(daysOfWeek);
      return `${minute} ${hour} * * ${dow}`;
    }
    case 'monthly':
      return `${minute} ${hour} ${dayOfMonth} * *`;
    case 'yearly':
      return `${minute} ${hour} ${dayOfMonth} ${month} *`;
  }
}

const range = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i);

const CRON_REGEX =
  /^[\d*/,-]+\s+[\d*/,-]+\s+[\d*/,-]+\s+[\d*/,-]+\s+[\d*/,-]+$/;

// Display Mon–Sat first, Sun last
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export function CronBuilder({
  value,
  onChange,
  disabled,
  language,
}: CronBuilderProps) {
  const [state, setState] = useState<CronState>(() => parseCron(value));
  const [rawInput, setRawInput] = useState(value || '0 8 * * 1-5');
  const lang = language === 'PL' ? 'PL' : 'EN';

  useEffect(() => {
    setState(parseCron(value));
    setRawInput(value || '0 8 * * 1-5');
  }, [value]);

  const update = (partial: Partial<CronState>) => {
    const next = { ...state, ...partial };
    setState(next);
    const built = buildCron(next);
    setRawInput(built);
    onChange(built);
  };

  const handleRawChange = (raw: string) => {
    setRawInput(raw);
    if (CRON_REGEX.test(raw.trim())) {
      const parsed = parseCron(raw.trim());
      setState(parsed);
      onChange(raw.trim());
    }
  };

  const dayLabels = lang === 'PL' ? DAYS_PL : DAYS_EN;
  const monthLabels = lang === 'PL' ? MONTHS_PL : MONTHS_EN;

  const toggleDay = (day: number) => {
    const next = state.daysOfWeek.includes(day)
      ? state.daysOfWeek.filter(d => d !== day)
      : [...state.daysOfWeek, day];
    update({ daysOfWeek: next });
  };

  const toggleHour = (h: number) => {
    const next = state.hours.includes(h)
      ? state.hours.filter(x => x !== h)
      : [...state.hours, h];
    update({ hours: next });
  };

  const currentCron = buildCron(state);
  let description: string;
  const descSource = CRON_REGEX.test(rawInput.trim())
    ? rawInput.trim()
    : currentCron;
  try {
    description = cronstrue.toString(descSource, {
      locale: lang === 'PL' ? 'pl' : 'en',
    });
  } catch {
    description = descSource;
  }

  return (
    <div className="cron-builder">
      <Row className="mb-3">
        <Col md={3}>
          <Form.Label>{t('period', lang)}</Form.Label>
          <Form.Select
            value={state.period}
            onChange={e => update({ period: e.target.value as PeriodType })}
            disabled={disabled}
          >
            {(Object.keys(PERIOD_LABELS) as PeriodType[]).map(p => (
              <option key={p} value={p}>
                {PERIOD_LABELS[p][lang]}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      <Row className="mb-3 align-items-end">
        {state.period === 'hourly' && (
          <Col md={2}>
            <Form.Label>{t('minuteStep', lang)}</Form.Label>
            <Form.Select
              value={state.minute}
              onChange={e => update({ minute: Number(e.target.value) })}
              disabled={disabled}
            >
              {[1, 2, 5, 10, 15, 20, 30].map(m => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}

        {(state.period === 'daily' ||
          state.period === 'weekly' ||
          state.period === 'monthly' ||
          state.period === 'yearly') && (
          <>
            <Col md={2}>
              <Form.Label>{t('hour', lang)}</Form.Label>
              <Form.Select
                value={state.hour}
                onChange={e => update({ hour: Number(e.target.value) })}
                disabled={disabled}
              >
                {range(0, 23).map(h => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>{t('minute', lang)}</Form.Label>
              <Form.Select
                value={state.minute}
                onChange={e => update({ minute: Number(e.target.value) })}
                disabled={disabled}
              >
                {range(0, 59).map(m => (
                  <option key={m} value={m}>
                    {String(m).padStart(2, '0')}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </>
        )}

        {(state.period === 'monthly' || state.period === 'yearly') && (
          <Col md={2}>
            <Form.Label>{t('day', lang)}</Form.Label>
            <Form.Select
              value={state.dayOfMonth}
              onChange={e => update({ dayOfMonth: Number(e.target.value) })}
              disabled={disabled}
            >
              {range(1, 31).map(d => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}

        {state.period === 'yearly' && (
          <Col md={3}>
            <Form.Label>{t('month', lang)}</Form.Label>
            <Form.Select
              value={state.month}
              onChange={e => update({ month: Number(e.target.value) })}
              disabled={disabled}
            >
              {range(1, 12).map(m => (
                <option key={m} value={m}>
                  {monthLabels[m - 1]}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}
      </Row>

      {state.period === 'hourly' && (
        <Row className="mb-2">
          <Col>
            <Form.Label>
              {t('hours', lang)}{' '}
              <small className="text-muted fw-normal">
                {'('}
                {state.hours.length === 0
                  ? t('hourAll', lang)
                  : state.hours
                      .sort((a, b) => a - b)
                      .map(h => String(h).padStart(2, '0'))
                      .join(', ')}
                {')'}
              </small>
            </Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {range(0, 23).map(h => (
                <Form.Check
                  key={h}
                  type="checkbox"
                  id={`hour-${h}`}
                  label={String(h).padStart(2, '0')}
                  checked={state.hours.includes(h)}
                  onChange={() => toggleHour(h)}
                  disabled={disabled}
                  inline
                />
              ))}
            </div>
          </Col>
        </Row>
      )}

      {(state.period === 'weekly' || state.period === 'hourly') && (
        <Row className="mb-3">
          <Col>
            <Form.Label>
              {t('daysOfWeek', lang)}
              {state.period === 'hourly' && (
                <small className="text-muted fw-normal">
                  {' '}
                  {'('}
                  {state.daysOfWeek.length === 0
                    ? lang === 'PL'
                      ? 'wszystkie'
                      : 'all'
                    : [...state.daysOfWeek]
                        .sort((a, b) => a - b)
                        .map(d => dayLabels[d])
                        .join(', ')}
                  {')'}
                </small>
              )}
            </Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {DAY_ORDER.map(idx => (
                <Form.Check
                  key={idx}
                  type="checkbox"
                  id={`dow-${idx}`}
                  label={dayLabels[idx]}
                  checked={state.daysOfWeek.includes(idx)}
                  onChange={() => toggleDay(idx)}
                  disabled={disabled}
                  inline
                />
              ))}
            </div>
          </Col>
        </Row>
      )}

      <Row className="mt-2 align-items-center">
        <Col md={4}>
          <Form.Label className="mb-1">
            <b>{t('cronExpr', lang)}</b>
          </Form.Label>
          <Form.Control
            type="text"
            value={rawInput}
            onChange={e => handleRawChange(e.target.value)}
            disabled={disabled}
            isInvalid={!CRON_REGEX.test(rawInput.trim())}
            placeholder="0 8 * * 1-5"
            style={{ fontFamily: 'monospace' }}
          />
        </Col>
        <Col className="pt-4">
          <small className="text-muted">{description}</small>
        </Col>
      </Row>
    </div>
  );
}
